import i18n from '@/translations/config';
import { toSnakeCase } from '@/utils/string.util';

// INFO: Prefer the translated code so inline messages don't repeat the field
// label the input is already showing.
const resolveMessage = (msg) => {
  if (typeof msg === 'string') {
    return msg;
  }

  if (!msg || typeof msg !== 'object') {
    return null;
  }

  const { code, message } = msg;

  if (code) {
    return i18n.t(`codes.${code}`, { defaultValue: message || code });
  }

  return message || null;
};

// INFO: Only the leading identifier is converted, so bracketed ids such as
// `customPaymentTypesMappings[<id>]` keep their contents byte-identical.
export const toSnakeCasePath = (path) => {
  if (!path || typeof path !== 'string') {
    return path;
  }

  const match = /^([A-Za-z0-9_]+)(.*)$/.exec(path);
  if (!match) {
    return path;
  }

  const [, head, rest] = match;
  return toSnakeCase(head) + rest;
};

const identity = (path) => path;

const addFieldError = (result, fieldName, message) => {
  if (!fieldName || !message) {
    return;
  }

  if (!result.fieldErrorMessages[fieldName]) {
    result.fieldErrorMessages[fieldName] = [];
  }

  if (!result.fieldErrorMessages[fieldName].includes(message)) {
    result.fieldErrorMessages[fieldName].push(message);
  }

  // INFO: First-wins single string, for the screens that predate
  // `fieldErrorMessages`.
  if (!result.fieldErrors[fieldName]) {
    result.fieldErrors[fieldName] = message;
  }
};

export const parseValidationErrors = (errorResponse, options = {}) => {
  const normalizePath = options.normalizePath || identity;

  const result = {
    fieldErrors: {},
    fieldErrorMessages: {},
    generalErrors: [],
  };

  if (!errorResponse || !Array.isArray(errorResponse.errors)) {
    if (errorResponse?.message) {
      result.generalErrors.push(errorResponse.message);
    }
    return result;
  }

  errorResponse.errors.forEach((error) => {
    const message = resolveMessage(error.msg);

    if (!message) {
      return;
    }

    const hasPath = error.path && error.path.trim() !== '';
    const hasAffectedFields =
      error.affected_fields &&
      Array.isArray(error.affected_fields) &&
      error.affected_fields.length > 0;

    if (hasPath) {
      addFieldError(result, normalizePath(error.path.trim()), message);
    }

    if (hasAffectedFields) {
      error.affected_fields.forEach((fieldName) => {
        addFieldError(result, normalizePath(fieldName), message);
      });
    }

    if (!hasPath && !hasAffectedFields) {
      result.generalErrors.push(message);
    }
  });

  return result;
};

const API_FIELD_TO_FORM_FIELD = {
  client_transaction_id: 'clientTransactionId',
  refund_to_address_line1: 'refundToAddressLine1',
  refund_to_address_line2: 'refundToAddressLine2',
  refund_to_city: 'city',
  refund_to_state: 'state',
  refund_to_zip: 'refundToZip',
  refund_to_country: 'refundToCountry',
  general_ledger: 'generalLedger',
  vendor_id_number: 'vendorIdNumber',
  payee_account_number: 'payeeAccountNumber',
  business_unit_id: 'businessUnitId',
  client_bank_account_id: 'clientBankAccountId',
  custom_payment_type_id: 'customPaymentTypeId',
  entity_type: 'entityType',
  line_items: 'lineItems',
};

const toFormFieldName = (fieldName) => {
  if (!fieldName) return fieldName;
  return API_FIELD_TO_FORM_FIELD[fieldName] || fieldName;
};

const appendFieldError = (fieldErrors, fieldName, message) => {
  if (!fieldName || !message) return;

  const formFieldName = toFormFieldName(fieldName);
  if (!fieldErrors[formFieldName]) {
    fieldErrors[formFieldName] = { messages: [] };
  }
  fieldErrors[formFieldName].messages.push(message);
};

export const normalizeFieldErrors = (errorResponse) => {
  const errors = errorResponse?.errors;

  if (!errors) {
    return {};
  }

  if (!Array.isArray(errors) && typeof errors === 'object') {
    return errors;
  }

  if (!Array.isArray(errors)) {
    return {};
  }

  const fieldErrors = {};

  // INFO: Supports standard error arrays after legacy field-error objects are
  // returned above.
  errors.forEach((error) => {
    appendFieldError(fieldErrors, error.path, error.msg || error.message);

    if (Array.isArray(error.affected_fields)) {
      error.affected_fields.forEach((fieldName) => {
        appendFieldError(fieldErrors, fieldName, error.msg || error.message);
      });
    }
  });

  return fieldErrors;
};

export const isValidationError = (errorResponse) => {
  return (
    errorResponse &&
    (errorResponse.error_code === 'VALIDATION_FAILED' ||
      Array.isArray(errorResponse.errors))
  );
};

export const getGeneralErrorMessage = (errorResponse, defaultMessage) => {
  if (!errorResponse) {
    return defaultMessage;
  }

  return (
    errorResponse.message ||
    errorResponse.msg ||
    errorResponse.error ||
    defaultMessage
  );
};
